/**
 * WizardHeader — the shared header for the add-account drawer: title + step label,
 * an optional Back button, an `actions` slot (e.g. Test/Save on the configure
 * step), and an inline close. Presentational; rendered inside a Sheet.
 */
import { X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/_shared/components/ui/button';
import { SheetClose, SheetDescription, SheetHeader, SheetTitle } from '@/_shared/components/ui/sheet';

interface WizardHeaderProps {
  title: string;
  badge?: React.ReactNode;
  stepLabel?: string;
  onBack?: () => void;
  actions?: React.ReactNode;
  showControls?: boolean;
}

export function WizardHeader({
  title,
  badge,
  stepLabel,
  onBack,
  actions,
  showControls = true,
}: WizardHeaderProps): React.JSX.Element {
  return (
    <SheetHeader className="gap-1 border-b border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <SheetTitle className="min-w-0 truncate text-base font-medium text-foreground">{title}</SheetTitle>
          {badge}
        </div>
        {showControls ? <WizardActions onBack={onBack} actions={actions} /> : <WizardClose />}
      </div>
      {stepLabel && <SheetDescription>{stepLabel}</SheetDescription>}
    </SheetHeader>
  );
}

/** The only close control of a step whose actions sit in the footer. */
function WizardClose(): React.JSX.Element {
  return (
    <SheetClose asChild>
      <Button type="button" variant="tertiary" iconOnly aria-label="Close" Icon={<X />} />
    </SheetClose>
  );
}

export function WizardActions({
  onBack,
  actions,
  inFooter = false,
}: Pick<WizardHeaderProps, 'onBack' | 'actions'> & {
  /** Footer actions are right-aligned, primary last, and carry no close: the header has it. */
  inFooter?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={
        inFooter ? 'flex flex-wrap items-center justify-end gap-1.5' : 'flex shrink-0 flex-wrap items-center gap-1.5'
      }
    >
      {onBack && <Button type="button" variant="secondary" onClick={onBack} label="Back" />}
      {actions}
      {!inFooter && <WizardClose />}
    </div>
  );
}
